import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { createI18n } from 'vue-i18n'
import router from './router'
import App from './App.vue'
import * as Sentry from '@sentry/vue'

// 引入 Vant 组件库
import Vant from 'vant'
import 'vant/lib/index.css'

// 引入 UnoCSS 样式
import 'virtual:uno.css'

// 引入全局样式
import './styles/index.scss'

// 引入 flexible
import './utils/flexible'

// 引入全局弹窗管理器
import { openStoreDialog, closeStoreDialog } from './composables/useStoreDialog'
// 引入体力不足弹窗管理器
import { openInsufficientPhysicalStrengthDialog, closeInsufficientPhysicalStrengthDialog } from './composables/useInsufficientPhysicalStrengthDialog'

import { startVersionCheck } from '@/utils/version-checker'
// 引入全局 Toast 工具
import toast from './utils/toast'
import dialogPlugin from '@/composables/useDialog'
import loginDialogPlugin from '@/composables/useLoginDialog'
import { decryptCommonParam } from '@/utils/appLogin'

// 引入长按指令
import longpress from './directives/longpress'
import { vHammer } from './directives/v-hammer'

// 引入 Firebase
import { initFirebase } from './plugins/firebase'

// 引入国际化配置
import { messages } from './locales'

// 创建应用实例
const app = createApp(App)

// 创建 Pinia 实例
const pinia = createPinia()
// 注册插件
app.use(pinia)
// 初始化 Firebase
initFirebase(router)
startVersionCheck(1)
// 创建 i18n 实例
const i18n = createI18n({
  legacy: false, // 使用 Composition API
  locale: 'en-US', // 强制使用英文
  fallbackLocale: 'en-US', // 备用语言也设为英文
  messages
})

Sentry.init({
  app,
  dsn: 'https://d3e15b54e4364066221053d99b9cdf14@o4510713453936640.ingest.us.sentry.io/4510713580617728',
  sendDefaultPii: true,
  environment: import.meta.env.MODE, // 'development' 或 'production'
  integrations: [Sentry.browserTracingIntegration({ router }), Sentry.replayIntegration()],
  tracesSampleRate: 1.0,
  replaysSessionSampleRate: 0.1, // 生产环境 10% 采样
  replaysOnErrorSampleRate: 1.0
})

// 捕获 Vue 渲染错误（白屏）
app.config.errorHandler = (err, instance, info) => {
  Sentry.captureException(err, {
    extra: { componentInfo: info }
  })
  console.error('Vue Error:', err, info)
}

// 捕获未处理的 Promise 错误
window.addEventListener('unhandledrejection', event => {
  Sentry.captureException(event.reason)
})


app.use(router)
app.use(i18n)
app.use(Vant)
app.use(dialogPlugin)
app.use(loginDialogPlugin)
app.directive('longpress', longpress)
app.directive('hammer', vHammer)
decryptCommonParam()
// 挂载全局 Toast 工具到 Vue 实例
app.config.globalProperties.$toast = toast

  // 初始化并挂载WebSocket管理器到全局


  // 挂载全局弹窗管理器到window对象
  ; (window as any).openStoreDialog = openStoreDialog
  ; (window as any).closeStoreDialog = closeStoreDialog
  ; (window as any).openInsufficientPhysicalStrengthDialog = openInsufficientPhysicalStrengthDialog
  ; (window as any).closeInsufficientPhysicalStrengthDialog = closeInsufficientPhysicalStrengthDialog

// 设置默认页面标题
document.title = 'Roleplay H5'


  // 启动应用：先挂载应用，然后在后台完成认证初始化
  ; (async () => {
    // 先挂载应用，让用户能立即看到界面（避免黑屏）
    app.mount('#app')
    localStorage.setItem('version', import.meta.env.VITE_APP_VERSION)
    // 认证初始化在后台异步进行，不阻塞界面渲染
    try {
      const { useChatStore } = await import('@/stores/chat')
      const chatStore = useChatStore()
      await chatStore.initFromIndexedDB()
      await chatStore.checkAndRecoverState()
    } catch (e) {
      console.error('Auth initialization failed:', e)
    }
  })()

// 初始化认证（游客自动登录等）

// 移动端调试
if (import.meta.env.DEV && !import.meta.env.VITE_DISABLE_DEBUG) {
  // 开发环境下显示调试信息
  // console.log('🚀 App mounted successfully!')
  // console.log('📱 Mobile H5 Development Mode')
}
